import React, { Fragment, useState, useEffect, useRef } from "react";
import Grid2 from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from '@mui/material/styles';
import { generateQRCode } from "../../action/qrcode";
import { getDeviceModelNumbersList } from './../../action/addvcModel'
import { useReactToPrint } from 'react-to-print';
import Alert from "../layout/Alert";
import { saveAs } from 'file-saver';
import CloudDownload from '@mui/icons-material/CloudDownload';
import { selectMenuProps } from '../../utils/selectMenuProps';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AppButton from "../Common/Button/Button";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import InfoField from "../Common/InfoField/InfoField";
import { SectionHeading, LabelText } from "../Common/Typography/AppTypography";
import DataTable from "../Common/DataTable/DataTable";

const maxPSNToSentInParallel = 30;

const formControlSx = (theme) => ({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: { maxWidth: 280 },
});
const labelColSx = { minWidth: { md: 140 }, flexShrink: 0 };
// `InputLabel` for MUI outlined selects can render slightly outside the control's
// box when shrunk; the previous `overflow: hidden` clipped it.
const inputColSx = { minWidth: 0, width: "100%", maxWidth: "100%", overflow: "visible" };

const headers = {
    modelId: "Model ID",
    productSerialNumber: "Product Serial Number",
    status: "QR Code Print Status",
    reasonOfFailure: "Reason of Failure"
};

const CreateQRCode = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    setAlert,
}) => {

    const theme = useTheme();

    const [category, setCategory] = useState("")
    const [brandName, setBrandName] = useState("")
    const [searchModelIdBy, setSearchModelIdBy] = useState("searchModelIdByMatter")
    const [modelId, setModelId] = useState("")
    const [startProductSerialNumber, setStartProductSerialNumber] = useState("")
    const [totalProductSerialNumbers, setTotalProductSerialNumbers] = useState(0)
    const [qrCodeCopies, setQRCodeCopies] = useState(4)
    const [qrSpacing, setQRSpacing] = useState(0.94)
    const [verticalSpacing, setVerticalSpacing] = useState(2.9)
    const [horizontalSpacing, setHorizontalSpacing] = useState(2.9)
    const [dpi, setDPI] = useState(500)

    const [uploadedPSNFileInput, setUploadedPSNFileInput] = useState("")
    const psnCSVFileInputRef = useRef()
    const [uploadedPSNListFromCSV, setUploadedPSNListFromCSV] = useState([])

    const [modelIdList, setModelIdList] = useState([])
    const [resultQRCode, setResultQRCode] = useState({})
    const [printQRCodes, setPrintQRCodes] = useState(false)
    const [totalQRCodeResponseReceived, setTotalQRCodeResponseReceived] = useState(0)
    const [validQRCodeResponseReceived, setValidQRCodeResponseReceived] = useState(0)
    const [qrCodeCreationInProgress, setQRCodeCreationInProgress] = useState(false)
    const [psnIndex, setPSNIndex] = useState(0)

    const [generateQRCodeButtonClicked, setGenerateQRCodeButtonClicked] = useState(false)

    const [psnQrStringMappingData, setPsnQrStringMappingData] = useState([["Product Serial Number", "QR", "Manual Code"]])

    const menuProps = selectMenuProps;

    useEffect(() => {
        if (!category || category.length===0 || category==="Select Category") {
            return
        }

        if (!brandName || brandName.length===0 || brandName==="Select Brand") {
            return
        }

        if (brandName && brandName.length!==0 && category && category.length!==0) {

            let isMatterEnabled = searchModelIdBy==="searchModelIdByMatter" ? 'true' : 'false'

            getDeviceModelNumbersList(brandName, category, isMatterEnabled)
                .then((data) => {
                    setModelIdList(data)
                    setModelId("")
                })
                .catch((err) => {
                    console.log('Error in Receiving the Model Number List For Category ' + category +
                        'Brand Name ' + brandName)

                    setModelId("")
                    setModelIdList([])
                })
        }
    }, [brandName, category, searchModelIdBy])

    useEffect(() => {
        if (totalQRCodeResponseReceived===0 || totalProductSerialNumbers===0) {
            return;
        }

        if (totalQRCodeResponseReceived===totalProductSerialNumbers) {
            console.log('All Response of PSN Received...')

            setQRCodeCreationInProgress(false)

            setGenerateQRCodeButtonClicked(false)

            if (validQRCodeResponseReceived > 0) {
                // print QR Codes only when the sucessful response of atleast one
                // PSN is received

                setPrintQRCodes(true)

                // also prepare the downloadable excel file of PSN and QR String Mapping
                createPSNQRStringMappingData()
            }
            else {
                // also prepare the downloadable excel file of PSN and QR String Mapping
                createPSNQRStringMappingData()

                setAlert("No Product Serial Number is Eligible for Print", 'danger')
                window.scrollTo(0, 0);
            }
        }
        else if (totalQRCodeResponseReceived % maxPSNToSentInParallel===0) {
            console.log('Sending Request for Next set of PSN, Total Response Received ' + totalQRCodeResponseReceived)

            // still PSN are pending for QR Code generation
            generateQRCodeInParallel()
        }
    }, [totalQRCodeResponseReceived])

    useEffect(() => {
        if (!printQRCodes) {
            return
        }

        // after the response of all the QR Codes are now received then go for 
        // printing the QR Codes.

        combineAndPrintImages()

    }, [printQRCodes])

    // use this effect when all the data is loaded in the array from CSV File
    useEffect(() => {
        if (uploadedPSNListFromCSV && uploadedPSNListFromCSV.length!==0) {
            generateMultipleQRCodes()
        }
    }, [uploadedPSNListFromCSV])

    useEffect(() => {
        if (generateQRCodeButtonClicked) {

            if (psnIndex===0 && printQRCodes===false && totalProductSerialNumbers===0 && validQRCodeResponseReceived===0 && totalQRCodeResponseReceived===0) {
                handleGenerateQRCode()
            }
            else {
                // reset other required parameters before generating QR Code
                setPSNIndex(0)

                // reset print QR Codes variable to false
                setPrintQRCodes(false)

                // First Clear the Table which shows the results of QR Codes
                setResultQRCode({})

                // set total Product Serial Numbers to 0
                setTotalProductSerialNumbers(0)

                // reset valid product serial numbers result
                setValidQRCodeResponseReceived(0)

                // Clear the Total Response received
                setTotalQRCodeResponseReceived(0)

                // reset the psn & qr string mapping data
                setPsnQrStringMappingData([["Product Serial Number", "QR", "Manual Code"]])
            }
        }
    }, [generateQRCodeButtonClicked])

    useEffect(() => {
        if (generateQRCodeButtonClicked) {
            if (psnIndex===0 && printQRCodes===false && totalProductSerialNumbers===0 && validQRCodeResponseReceived===0 && totalQRCodeResponseReceived===0) {
                handleGenerateQRCode()
            }
        }
    }, [psnIndex, printQRCodes, totalProductSerialNumbers, validQRCodeResponseReceived, totalQRCodeResponseReceived])

    function handleGenerateQRCodeReset() {
        setCategory("")
        setBrandName("")
        setModelId("")
        setSearchModelIdBy("searchModelIdByMatter")
        setModelIdList([])
        setStartProductSerialNumber("")
        setTotalProductSerialNumbers(0)
        setResultQRCode({})
        setPrintQRCodes(false)
        setValidQRCodeResponseReceived(0)
        setTotalQRCodeResponseReceived(0)
        setQRCodeCreationInProgress(false)

        // reset the file input states
        setUploadedPSNFileInput("")
        psnCSVFileInputRef.current.value = ""
        setUploadedPSNListFromCSV([])

        // set the default values of other input fields as default values
        setQRCodeCopies(4)
        setQRSpacing(0.94)
        setVerticalSpacing(2.9)
        setHorizontalSpacing(2.9)
        setDPI(500)

        setPSNIndex(0)

        setGenerateQRCodeButtonClicked(false)

        setPsnQrStringMappingData([["Product Serial Number", "QR", "Manual Code"]])
    }

    function handleGenerateQRCode() {

        let bIsUserInputFieldError = false

        // perform validation checks
        if (!category || category.length===0) {
            bIsUserInputFieldError = true
            setAlert('Category Name Cannot be Empty', 'danger')
        }
        else if (!brandName || brandName.length===0) {
            bIsUserInputFieldError = true
            setAlert('Brand Name Cannot be Empty', 'danger')
        }
        else if (!modelId || modelId.length===0) {
            bIsUserInputFieldError = true
            setAlert('Model ID Cannot be Empty', 'danger')
        }
        else if (!qrCodeCopies || qrCodeCopies <= 0 || qrCodeCopies > 5 || qrCodeCopies==="" || parseInt(qrCodeCopies)===NaN) {
            bIsUserInputFieldError = true
            setAlert('Invalid QR Code Copies Value. Should be Greater than 0 and Less than 5', 'danger')
        }
        else if (qrSpacing < 0 || parseInt(qrSpacing)===NaN) {
            bIsUserInputFieldError = true
            setAlert('Invalid QR Spacing Value. Should be Greater than  or Equal to 0', 'danger')
        }
        else if (!dpi || dpi <= 0 || dpi==="" || parseInt(dpi)===NaN) {
            bIsUserInputFieldError = true
            setAlert('Invalid DPI Value. Must be Greater than or Equal to 1', 'danger')
        }
        else if (verticalSpacing < 0 || parseInt(verticalSpacing)===NaN) {
            bIsUserInputFieldError = true
            setAlert('Invalid Vertical Spacing', 'danger')
        }
        else if (horizontalSpacing < 0 || parseInt(horizontalSpacing)===NaN) {
            bIsUserInputFieldError = true
            setAlert('Invalid Horizontal Spacing', 'danger')
        }

        // check for user infput fields error. If error then return
        if (bIsUserInputFieldError) {
            setGenerateQRCodeButtonClicked(false)
            window.scrollTo(0, 0);
            return
        }

        // Priority is given to file - means if file is not provided then single product serial number
        // otherwise file is considered

        if (!uploadedPSNFileInput || uploadedPSNFileInput==="") {

            setTotalProductSerialNumbers(1)

            // for single QR Code Generation mark QR Code creation process as in progress...
            setQRCodeCreationInProgress(true)

            generateSingleQRCode(startProductSerialNumber)
        }
        else {
            if (uploadedPSNFileInput.type==='text/csv') {
                generateMultipleQRCodes()
            }
            else {
                setAlert('Invalid File Type. Only CSV Files are supported.', 'danger')
                setGenerateQRCodeButtonClicked(false)
                window.scrollTo(0, 0);
                return
            }
        }
    }

    function generateSingleQRCode(productSerialNumber) {
        let bodyData = {
            'category': category,
            'modelId': JSON.parse(modelId)["modelId"],
            "dpi": parseInt(dpi),
            "count": parseInt(qrCodeCopies)
        }

        if (productSerialNumber && productSerialNumber.length > 0) {
            bodyData['productSerialNumber'] = productSerialNumber
        }

        if (parseInt(qrSpacing) > 0) {
            bodyData["horizontalSpacingInMM"] = parseInt(qrSpacing)
        }

        generateQRCode(bodyData)
            .then((data) => {
                setResultQRCode(prevState => {
                    return {
                        ...prevState,
                        [productSerialNumber]: {
                            'productSerialNumber': data?.productSerialNumber ?? "",
                            'status': 'Success',
                            'reasonOfFailure': "",
                            'qrImage': data.qrImage,
                            'modelId': data?.modelId ?? "",
                            'qrString': data?.qrString ?? "",
                            'manualCode': data?.manualCode ?? ""
                        }
                    }
                })
                setValidQRCodeResponseReceived(prevState => {
                    return prevState + 1
                })
                setTotalQRCodeResponseReceived(prevState => {
                    return prevState + 1
                })
            })
            .catch((err) => {
                setResultQRCode(prevState => {
                    return {
                        ...prevState,
                        [productSerialNumber]: {
                            'productSerialNumber': productSerialNumber && productSerialNumber.length > 0 ? productSerialNumber : "",
                            'status': 'Failure',
                            'reasonOfFailure': err?.response?.data?.message ?? err.message,
                            'modelId': JSON.parse(modelId)["modelId"],
                            'qrString': "",
                            'manualCode': ""
                        }
                    }
                })
                setTotalQRCodeResponseReceived(prevState => {
                    return prevState + 1
                })
            })
    }

    function generateMultipleQRCodes() {

        if (!uploadedPSNListFromCSV || uploadedPSNListFromCSV.length===0) {
            // get the list of product serial numbers if list of product serial 
            // number is not populated

            parseUploadedPSNFile()
            return
        }

        if (uploadedPSNListFromCSV && uploadedPSNListFromCSV.length===0) {
            setAlert('No Product Serial Number is Present in CSV File', 'danger')
            return
        }

        // set total product serial numbers in a range as 
        setTotalProductSerialNumbers(uploadedPSNListFromCSV.length)

        // mark QR Code Creation Process as in progress.. only after validation
        setQRCodeCreationInProgress(true)

        generateQRCodeInParallel()

    }

    function generateQRCodeInParallel() {

        let psnCountToGenerateQRCode = 0
        if (uploadedPSNListFromCSV.length - totalQRCodeResponseReceived > maxPSNToSentInParallel) {
            psnCountToGenerateQRCode = maxPSNToSentInParallel
        }
        else {
            psnCountToGenerateQRCode = uploadedPSNListFromCSV.length - totalQRCodeResponseReceived
        }

        console.log('Sending Request for PSN Count ' + psnCountToGenerateQRCode)

        for (let i = psnIndex; i < psnIndex + psnCountToGenerateQRCode; i++) {
            let psn = uploadedPSNListFromCSV[i]

            generateSingleQRCode(psn) // generate QR Code for each PSN one by one
        }

        // update the index for processing next set of PSN in parallel
        setPSNIndex((prevState) => {
            return prevState + psnCountToGenerateQRCode
        })
    }

    function parseUploadedPSNFile() {
        // parse uploaded file to get the list of product serial numbers
        const reader = new FileReader();
        reader.onload = function (event) {
            let textData = event.target.result

            let arrFileData = csvToArray(textData)
            if (arrFileData.length===0) {

                setQRCodeCreationInProgress(false)
                setGenerateQRCodeButtonClicked(false)

                setAlert("Empty File of Product Serial Numbers Uploaded", "danger")
                return
            }

            arrFileData = arrFileData.filter(element => Object.values(element)[0] && Object.values(element)[0]!=="" && Object.values(element)[0].length!==0).map((element) => {
                return Object.values(element)[0].replace(/(\r\n|\n|\r)/gm, '')
            })

            // set the product serial number data in state variable
            setUploadedPSNListFromCSV(arrFileData)
        };

        reader.readAsText(uploadedPSNFileInput);
    }

    function csvToArray(str, delimiter = ",") {

        const headers = str.slice(0, str.indexOf("\n")).split(delimiter);

        const rows = str.slice(str.indexOf("\n") + 1).split("\n");
        if (rows && rows.length > 0) {
            if (rows[rows.length - 1]==="") {
                rows.splice(-1) //removing the last element with empty values from array
            }
        }

        if (!rows || rows.length===0) {
            return []
        }

        const arr = rows.map(function (row) {
            const values = row.split(delimiter);
            const el = headers.reduce(function (object, header, index) {
                object[header] = values[index];
                return object;
            }, {});
            return el;
        });

        return arr;
    }


    // Function to convert the array to a CSV string
    const arrayToCSV = (data) => {
        return data.map(row => row.map(String).map(value => `"${value}"`).join(',')).join('\n');
    };

    // Function to download the CSV file
    const downloadCSV = () => {
        let csvData = ""
        if ((uploadedPSNListFromCSV && uploadedPSNListFromCSV.length > 0) || (startProductSerialNumber && startProductSerialNumber.length > 0)) {
            csvData = arrayToCSV(psnQrStringMappingData);
        }
        else {
            csvData = arrayToCSV([["Model Number", "Model Id", "QR", "Manual Code"], ...psnQrStringMappingData.slice(1)])
        }

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'qrcode.csv');
    };

    function combineAndPrintImages() {
        handlePrint()
    }

    // Printing Process
    const componentToPrintRef = useRef();
    const handlePrint = useReactToPrint({
        // content: () => {
        //     return componentToPrintRef.current
        // }

        // Updated due to version upgrade
        contentRef: componentToPrintRef,
    });

    async function createPSNQRStringMappingData() {
        if (uploadedPSNListFromCSV && uploadedPSNListFromCSV.length > 0) {
            uploadedPSNListFromCSV.map((element, index) => {
                setPsnQrStringMappingData((prevState) => [
                    ...prevState,
                    [resultQRCode[element].productSerialNumber,
                    resultQRCode[element].qrString,
                    resultQRCode[element].manualCode + "\t"
                    ]
                ])
            })
        }
        else {
            if ((uploadedPSNListFromCSV && uploadedPSNListFromCSV.length > 0) || (startProductSerialNumber && startProductSerialNumber.length > 0)) {
                Object.keys(resultQRCode).map((element, index) => {
                    setPsnQrStringMappingData((prevState) => [
                        ...prevState,
                        [resultQRCode[element].productSerialNumber,
                        resultQRCode[element].qrString,
                        resultQRCode[element].manualCode + "\t"
                        ]
                    ])
                })
            }
            else {
                Object.keys(resultQRCode).map((element, index) => {
                    setPsnQrStringMappingData((prevState) => [
                        ...prevState,
                        [JSON.parse(modelId)["modelNumber"],
                        resultQRCode[element].modelId,
                        resultQRCode[element].qrString,
                        resultQRCode[element].manualCode + "\t"
                        ]
                    ])
                })
            }
        }
    }

    return (
        <Fragment>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, boxShadow: 2, borderRadius: 1, mb: 2, width: "100%", minWidth: 0, boxSizing: "border-box", overflow: "visible" }}>
                <Alert />
                <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "visible" }}>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Category:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="select"
                                label="Select Category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                options={allowedDeviceCategories.map(cat => ({ label: cat, value: cat }))}
                                slotProps={{ select: { MenuProps: menuProps } }}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Brand:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="select"
                                label="Select Brand"
                                value={brandName}
                                onChange={(e) => setBrandName(e.target.value)}
                                options={allowedCompanyBrand.map(brand => ({ label: brand, value: brand }))}
                                slotProps={{ select: { MenuProps: menuProps } }}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Search Model Number By:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <AppRadioGroup
                                row
                                name="searchModelIdBy"
                                value={searchModelIdBy}
                                onChange={(e) => setSearchModelIdBy(e.target.value)}
                                formControlSx={{ ml: "-11px" }}
                                optionLabelSx={{ whiteSpace: "nowrap" }}
                                options={[
                                  { label: "Matter Enabled", value: "searchModelIdByMatter" },
                                  { label: "Non Matter Enabled", value: "searchModelIdByNonMatter" },
                                ]}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Model Number:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="select"
                                label="Select Model Number"
                                value={modelId}
                                onChange={(e) => setModelId(e.target.value)}
                                options={modelIdList.map(model => ({
                                    label: model.modelNumber,
                                    value: JSON.stringify({ modelId: model.modelId, modelNumber: model.modelNumber })
                                }))}
                                slotProps={{ select: { MenuProps: menuProps } }}
                            />
                        </Grid2>
                    </Grid2>
                    {modelId.length != 0 && (
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText strong>Model ID:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <LabelText>{JSON.parse(modelId)?.modelId ?? ""}</LabelText>
                        </Grid2>
                    </Grid2>
                    )}
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Product Serial Number:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput
                                label="Product Serial Number"
                                placeholder="Enter Product Serial Number"
                                value={startProductSerialNumber}
                                onChange={(e) => setStartProductSerialNumber(e.target.value)}
                                sx={formControlSx(theme)}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={labelColSx} />
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <Typography variant="body2" color="text.secondary">OR</Typography>
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText sx={{ fontWeight: 600 }}>Upload CSV File:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <Box sx={formControlSx(theme)}>
                                <input
                                    ref={psnCSVFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => {
                                        setUploadedPSNListFromCSV([])
                                        setUploadedPSNFileInput(e.target.files?.[0])
                                    }}
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        padding: 8,
                                        border: "1px solid rgba(0,0,0,0.42)",
                                        borderRadius: 4,
                                    }}
                                />
                                {uploadedPSNListFromCSV.length > 0 && (
                                    <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
                                        <strong>Total PSNs:</strong> {uploadedPSNListFromCSV.length}
                                    </Typography>
                                )}
                            </Box>
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>QR Code Copies:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="number"
                                value={qrCodeCopies}
                                onChange={(e) => setQRCodeCopies(e.target.value)}
                                onKeyDown={(e) => {
                                    ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault();
                                }}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>QR Spacing (In mm):</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="number"
                                value={qrSpacing}
                                onChange={(e) => setQRSpacing(e.target.value)}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>Spacing (In mm):</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap", gap: { xs: 1.5, sm: 2 }, maxWidth: { xs: "100%", md: 280 }, width: "100%", minWidth: 0 }}>
                                <Box sx={{ width: { xs: "100%", sm: "auto" }, flex: { sm: "1 1 100px" }, minWidth: 0, maxWidth: { sm: 130 } }}>
                                    <LabelText sx={{ fontSize: 12, mb: 0.5, display: "block" }}>Vertical Spacing</LabelText>
                                    <FormInput
                                        placeholder="2.9"
                                        type="number"
                                        value={verticalSpacing}
                                        onChange={(e) => setVerticalSpacing(e.target.value)}
                                        sx={{ width: "100%" }}
                                    />
                                </Box>
                                <Box sx={{ width: { xs: "100%", sm: "auto" }, flex: { sm: "1 1 100px" }, minWidth: 0, maxWidth: { sm: 130 } }}>
                                    <LabelText sx={{ fontSize: 12, mb: 0.5, display: "block" }}>Horizontal Spacing</LabelText>
                                    <FormInput
                                        placeholder="2.9"
                                        type="number"
                                        value={horizontalSpacing}
                                        onChange={(e) => setHorizontalSpacing(e.target.value)}
                                        sx={{ width: "100%" }}
                                    />
                                </Box>
                            </Box>
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={{ xs: 12, md: 2 }} sx={{ ...labelColSx, alignSelf: { md: "center" } }}>
                            <LabelText>DPI:</LabelText>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 10 }} sx={inputColSx}>
                            <FormInput sx={formControlSx(theme)}
                                type="number"
                                value={dpi}
                                onChange={(e) => setDPI(e.target.value)}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} sx={{ mt: 1, mb: 2, alignItems: "center", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                        <Grid2 size={12}>
                            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, flexWrap: "wrap", gap: 1.5, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "flex-start", width: "100%", minWidth: 0, maxWidth: "100%" }}>
                                <AppButton
                                    variant="primary"
                                    onClick={() => {
                                        setGenerateQRCodeButtonClicked(true)
                                    }}
                                    disabled={qrCodeCreationInProgress ? true : false}
                                >
                                    {
                                        qrCodeCreationInProgress ? <Fragment>
                                            <CircularProgress size="2em" style={{ color: "#AFB42B", marginTop: "5px" }} />
                                        </Fragment>
                                            : "Generate QR Code"
                                    }
                                </AppButton>
                                <AppButton
                                    variant="ghost"
                                    onClick={handleGenerateQRCodeReset}
                                    disabled={qrCodeCreationInProgress ? true : false}
                                >
                                    Reset
                                </AppButton>
                            </Box>
                        </Grid2>
                    </Grid2>
                </Box>
            </Paper>
            {/* Component to Print QR Code. Here Print the QR Codes but donot display it to users.
                Directly print them in the print console window
            */}
            <div style={{ display: 'none' }}>
                <ComponentToPrint ref={componentToPrintRef} uploadedPSNListFromCSV={uploadedPSNListFromCSV} resultQRCode={resultQRCode} vSpacing={verticalSpacing} hSpacing={horizontalSpacing} dpi={dpi} />
            </div>
            {
                resultQRCode && Object.keys(resultQRCode).length > 0 &&
                <Fragment>
                    <Paper sx={{ p: { xs: 1.5, sm: 2 }, boxShadow: 2, borderRadius: 1, mb: 2, width: "100%", minWidth: 0, boxSizing: "border-box", overflow: "hidden" }}>
                        <SectionHeading style={{ marginTop: '20px', marginBottom: '16px' }}>
                            QR Code Print Status Details
                        </SectionHeading>

                        <Grid2 container spacing={2} sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
                            <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                                <InfoField label="Total QR Codes" value={totalQRCodeResponseReceived} />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                                <InfoField label="Success" value={validQRCodeResponseReceived} />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                                <InfoField label="Failed" value={totalQRCodeResponseReceived - validQRCodeResponseReceived} />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                                <InfoField label="Download Result" value={
                                    totalQRCodeResponseReceived === totalProductSerialNumbers
                                        ? <AppButton variant="primary" onClick={downloadCSV}>
                                            Download <CloudDownload style={{ marginLeft: '10px', verticalAlign: 'sub' }} />
                                          </AppButton>
                                        : "File Generation In Progress"
                                } />
                            </Grid2>
                        </Grid2>
                    </Paper>
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
                        <DataTable
                            columns={
                                ((!uploadedPSNFileInput || uploadedPSNFileInput === "") &&
                                    (!startProductSerialNumber || startProductSerialNumber.trim() === ""))
                                    ? ["modelId", "status", "reasonOfFailure"].map((key) => ({
                                        key,
                                        label: headers[key],
                                        cellSx: { wordBreak: "break-word", whiteSpace: "normal" }
                                    }))
                                    : ["productSerialNumber", "status", "reasonOfFailure"].map((key) => ({
                                        key,
                                        label: headers[key],
                                        cellSx: { wordBreak: "break-word", whiteSpace: "normal" }
                                    }))
                            }
                            data={
                                uploadedPSNListFromCSV && uploadedPSNListFromCSV.length > 0
                                    ? (totalProductSerialNumbers === totalQRCodeResponseReceived)
                                        ? uploadedPSNListFromCSV.map((psn) => resultQRCode[psn] && { ...resultQRCode[psn], _rowId: psn }).filter(Boolean)
                                        : []
                                    : Object.keys(resultQRCode).map((key) => ({ ...resultQRCode[key], _rowId: key }))
                            }
                            rowKey="_rowId"
                            maxHeight="58vh"
                            stickyHeader
                        />
                    </Paper>
                </Fragment>
            }
        </Fragment>
    );
}

const ComponentToPrint = React.forwardRef((props, ref) => (
    <div ref={ref}>
        {
            props.uploadedPSNListFromCSV && props.uploadedPSNListFromCSV.length > 0 ?
                props.uploadedPSNListFromCSV.map((psn) => {
                    if (props.resultQRCode[psn] && props.resultQRCode[psn].qrImage && props.resultQRCode[psn].qrImage!=="") {
                        return <PrintTemplate qrCodeDetails={props.resultQRCode[psn]} vSpacing={parseInt(props.vSpacing)} hSpacing={parseInt(props.hSpacing)} dpi={parseInt(props.dpi)} />
                    }
                })
                :
                props.resultQRCode && Object.keys(props.resultQRCode).map((psn) => {
                    if (props.resultQRCode[psn] && props.resultQRCode[psn].qrImage && props.resultQRCode[psn].qrImage!=="") {
                        return <PrintTemplate qrCodeDetails={props.resultQRCode[psn]} vSpacing={parseInt(props.vSpacing)} hSpacing={parseInt(props.hSpacing)} dpi={parseInt(props.dpi)} />
                    }
                })
        }
    </div>
));

const PrintTemplate = ({ qrCodeDetails, vSpacing, hSpacing, dpi }) => {

    function getContent() {
        let vSpacingInPixels = "", hSpacingInPixels = ""

        if (parseInt(vSpacing)===0) {
            vSpacingInPixels = "0px"
        }
        else {
            vSpacingInPixels = (vSpacing * (dpi / 25.4)) + "px"
        }

        if (parseInt(hSpacing)===0) {
            hSpacingInPixels = "0px"
        }
        else {
            hSpacingInPixels = (hSpacing * (dpi / 25.4)) + "px"
        }

        return <div>
            <img src={"data:image/png;base64," + qrCodeDetails.qrImage}
                style={{ position: 'relative', marginTop: vSpacingInPixels, marginLeft: hSpacingInPixels }} />
        </div>
    }

    return (
        <Fragment>
            <div>
                {getContent()}
            </div>
        </Fragment>
    )
};

export default CreateQRCode